import React, { useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import MultipleSelect, { MultipleSelectItem } from 'components/selectors/MultipleSelect';
import BzmDateRangePicker, {
  DateRange,
  FixedTimeRange,
} from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmDateRangePicker';
import {
  lastYearWholeMonthRange,
  minSelectableDate,
  periodToDisplayRange,
  snapRangeToGranularity,
} from 'views/Admin/_shared/dateApi';
import { PeriodPreset, PeriodSelection, UsageWorkspace } from 'views/Admin/UsageReport/types';
import { RoiFilters } from './types';

interface Props {
  filters: RoiFilters;
  workspaces: UsageWorkspace[];
  onChange: (next: RoiFilters) => void;
  onDownloadPdf: () => void;
}

const USAGE_PRESETS: PeriodPreset[] = ['LAST_MONTH', 'LAST_QUARTER', 'LAST_6_MONTHS', 'LAST_YEAR'];

const periodToFixed = (p: PeriodSelection): FixedTimeRange | null =>
  p.kind === 'preset' ? (p.value as FixedTimeRange) : null;

const isUsagePreset = (value: FixedTimeRange): value is PeriodPreset =>
  (USAGE_PRESETS as readonly string[]).includes(value);

const RoiInsightsFilters: React.FC<Props> = ({ filters, workspaces, onChange, onDownloadPdf }) => {
  const { t } = useTranslation('admin');

  const presetButtons = [
    { optionValue: 'LAST_MONTH' as FixedTimeRange, label: t('roiInsights.lastMonth') },
    { optionValue: 'LAST_QUARTER' as FixedTimeRange, label: t('roiInsights.lastQuarter') },
    { optionValue: 'LAST_6_MONTHS' as FixedTimeRange, label: t('roiInsights.last6Months') },
    { optionValue: 'LAST_YEAR' as FixedTimeRange, label: t('roiInsights.lastYear') },
  ];

  const handleFixedRangeChanged = (preset: FixedTimeRange) => {
    if (!isUsagePreset(preset)) return;
    if (preset === 'LAST_YEAR') {
      // Older than 6 months → monthly granularity: send a whole-month range the backend accepts.
      const range = lastYearWholeMonthRange();
      onChange({ ...filters, period: { kind: 'custom', from: range.from, to: range.to } });
      return;
    }
    onChange({ ...filters, period: { kind: 'preset', value: preset } });
  };

  const handleCustomRangeChanged = (range: DateRange) => {
    // BzmDateRangePicker also fires this on a preset click, with a non-midnight `to` (= now);
    // ignore that echo so a preset is applied once, via fixedSelectionChanged.
    if (range.to.getHours() !== 0 || range.to.getMinutes() !== 0 || range.to.getSeconds() !== 0) {
      return;
    }
    const snapped = snapRangeToGranularity(range);
    onChange({ ...filters, period: { kind: 'custom', from: snapped.from, to: snapped.to } });
  };

  const workspaceItems: MultipleSelectItem[] = workspaces.map(w => ({ value: String(w.id), label: w.name }));
  const selectedWorkspaceItems: MultipleSelectItem[] = workspaceItems.filter(item =>
    filters.workspaceIds.includes(Number(item.value)),
  );

  const handleWorkspaceChange = (selected: MultipleSelectItem[] | MultipleSelectItem) => {
    const items = selected == null ? [] : ([] as MultipleSelectItem[]).concat(selected as MultipleSelectItem[]);
    onChange({ ...filters, workspaceIds: items.map(item => Number(item.value)) });
  };

  const displayRange: DateRange = useMemo(() => periodToDisplayRange(filters.period), [filters.period]);

  return (
    <div className="roi-filters d-flex align-items-start">
      <div className="toolbar-field">
        <label>{t('roiInsights.toolbar.timeFrameLabel')}</label>
        <BzmDateRangePicker
          presets={presetButtons}
          presetsInPopover={true}
          showTimePicker={false}
          isCompact={true}
          fixedSelection={periodToFixed(filters.period)}
          dateRange={displayRange}
          fixedSelectionChanged={handleFixedRangeChanged}
          dateRangeChanged={handleCustomRangeChanged}
          minSelectableDate={minSelectableDate()}
        />
      </div>

      <div className="toolbar-field" style={{ maxWidth: 360, flexGrow: 1 }}>
        <label>{t('roiInsights.toolbar.workspaceLabel')}</label>
        <MultipleSelect
          items={workspaceItems}
          selected={selectedWorkspaceItems}
          onChange={handleWorkspaceChange}
          placeholder={t('roiInsights.allWorkspaces')}
          isMulti={true}
        />
      </div>

      <Button variant="primary" className="ml-auto" onClick={onDownloadPdf}>
        {t('roiInsights.downloadPdf')}
      </Button>
    </div>
  );
};

export default RoiInsightsFilters;
